<script>
import { isEmpty } from 'lodash-es';
import { GlAvatarLink, GlAvatar } from '@gitlab/ui';
import DuoQuestionNote from 'ee_else_ce/work_items/components/notes/duo_question_note.vue';
import * as Sentry from '~/sentry/sentry_browser_wrapper';
import toast from '~/vue_shared/plugins/global_toast';
import { __ } from '~/locale';
import Tracking from '~/tracking';
import { updateDraft, clearDraft, getDraft } from '~/lib/utils/autosave';
import { renderMarkdown } from '~/notes/utils';
import { getLocationHash } from '~/lib/utils/url_utility';
import { getIdFromGraphQLId } from '~/graphql_shared/utils';
import gfmEventHub from '~/vue_shared/components/markdown/eventhub';
import EditedAt from '~/issues/show/components/edited.vue';
import TimelineEntryItem from '~/vue_shared/components/notes/timeline_entry_item.vue';
import NoteHeader from '~/notes/components/note_header.vue';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import { i18n, TRACKING_CATEGORY_SHOW, VIEW_CONTEXT } from '../../constants';
import updateWorkItemMutation from '../../graphql/update_work_item.mutation.graphql';
import updateWorkItemNoteMutation from '../../graphql/notes/update_work_item_note.mutation.graphql';
import workItemByIidQuery from '../../graphql/work_item_by_iid.query.graphql';
import { isAssigneesWidget } from '../../utils';
import WorkItemCommentForm from './work_item_comment_form.vue';
import NoteActions from './work_item_note_actions.vue';
import WorkItemNoteAwardsList from './work_item_note_awards_list.vue';
import NoteBody from './work_item_note_body.vue';

export default {
  name: 'WorkItemNoteThread',
  components: {
    DuoQuestionNote,
    WorkItemNoteAwardsList,
    TimelineEntryItem,
    NoteBody,
    NoteHeader,
    NoteActions,
    GlAvatar,
    GlAvatarLink,
    WorkItemCommentForm,
    EditedAt,
  },
  mixins: [glFeatureFlagsMixin(), Tracking.mixin()],
  inject: {
    viewContext: { default: VIEW_CONTEXT.fullScreen },
  },
  props: {
    fullPath: {
      type: String,
      required: true,
    },
    workItemId: {
      type: String,
      required: true,
    },
    workItemIid: {
      type: String,
      required: true,
    },
    note: {
      type: Object,
      required: true,
    },
    isFirstNote: {
      type: Boolean,
      required: false,
      default: false,
    },
    isOnlyCommentOfAThread: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasReplies: {
      type: Boolean,
      required: false,
      default: false,
    },
    discussionId: {
      type: String,
      required: false,
      default: '',
    },
    // Replies are read only by the EE Duo question card, which needs the answer
    // already posted in this thread to render its answered state after a reload.
    replies: {
      type: Array,
      required: false,
      default: () => [],
    },
    workItemType: {
      type: String,
      required: true,
    },
    markdownPreviewPath: {
      type: String,
      required: true,
    },
    newCommentTemplatePaths: {
      type: Array,
      required: false,
      default: () => [],
    },
    autocompleteDataSources: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    assignees: {
      type: Array,
      required: false,
      default: () => [],
    },
    canReply: {
      type: Boolean,
      required: false,
      default: false,
    },
    canSetWorkItemMetadata: {
      type: Boolean,
      required: false,
      default: false,
    },
    isDiscussionResolved: {
      type: Boolean,
      required: false,
      default: false,
    },
    isDiscussionResolvable: {
      type: Boolean,
      required: false,
      default: false,
    },
    isResolving: {
      type: Boolean,
      required: false,
      default: false,
    },
    resolvedBy: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    hideFullscreenMarkdownButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    uploadsPath: {
      type: String,
      required: true,
    },
  },
  emits: [
    'cancel-editing',
    'delete-note',
    'error',
    'report-abuse',
    'resolve',
    'start-editing',
    'start-replying',
  ],
  data() {
    return {
      isEditing: false,
      isUpdating: false,
      workItem: {},
    };
  },
  computed: {
    // eslint-disable-next-line vue/no-unused-properties
    tracking() {
      return {
        category: TRACKING_CATEGORY_SHOW,
        label: 'work_item_note_actions',
        property: `type_${this.workItemType}`,
        extra: { viewContext: this.viewContext },
      };
    },
    author() {
      return this.note.author || {};
    },
    hasAuthor() {
      return !isEmpty(this.author);
    },
    authorId() {
      return getIdFromGraphQLId(this.author.id);
    },
    externalAuthor() {
      return this.note?.externalAuthor;
    },
    entryClass() {
      return {
        'note note-wrapper note-comment': true,
        target: this.isTarget,
        'inner-target': this.isTarget && !this.isFirstNote,
        'internal-note': this.note.internal,
      };
    },
    showReply() {
      return this.canReply && this.isFirstNote;
    },
    canResolve() {
      return this.isDiscussionResolvable && this.isFirstNote && this.hasReplies;
    },
    noteHeaderClass() {
      return {
        'note-header': true,
      };
    },
    autosaveKey() {
      // eslint-disable-next-line @gitlab/require-i18n-strings
      return `${this.note.id}-comment`;
    },
    autosaveKeyInternalNote() {
      return `${this.note.id}-internal-note`;
    },
    lastEditedBy() {
      return this.note.lastEditedBy;
    },
    hasAdminPermission() {
      return this.note.userPermissions.adminNote;
    },
    noteAnchorId() {
      return `note_${getIdFromGraphQLId(this.note.id)}`;
    },
    isTarget() {
      return getLocationHash() === this.noteAnchorId;
    },
    noteUrl() {
      const routeParamType = this.$route?.params?.type;
      if (routeParamType && !this.note.url.includes(routeParamType)) {
        return this.note.url.replace('work_items', routeParamType);
      }
      return this.note.url;
    },
    hasAwardEmojiPermission() {
      return this.note.userPermissions.awardEmoji;
    },
    isAuthorAnAssignee() {
      return Boolean(this.assignees.filter((assignee) => assignee.id === this.author.id).length);
    },
    currentUserId() {
      return window.gon.current_user_id;
    },
    isCurrentUserAuthorOfNote() {
      return this.authorId === this.currentUserId;
    },
    isWorkItemAuthor() {
      return getIdFromGraphQLId(this.workItem.author?.id) === this.authorId;
    },
    projectName() {
      return this.workItem.namespace?.name;
    },
    isWorkItemConfidential() {
      return this.workItem.confidential;
    },
    duoCreatedSessionId() {
      return getIdFromGraphQLId(this.note.duoCreatedSession?.id);
    },
  },
  mounted() {
    gfmEventHub.$on('edit-note', this.handleEditNote);
  },
  beforeDestroy() {
    gfmEventHub.$off('edit-note', this.handleEditNote);
  },

  apollo: {
    workItem: {
      query: workItemByIidQuery,
      variables() {
        return {
          fullPath: this.fullPath,
          iid: this.workItemIid,
          useWorkItemFeatures: Boolean(this.glFeatures?.workItemFeaturesField),
        };
      },
      update(data) {
        return data.namespace?.workItem ?? {};
      },
      skip() {
        return !this.workItemIid;
      },
      error() {
        this.$emit('error', i18n.fetchError);
      },
    },
  },
  methods: {
    showReplyForm() {
      this.$emit('start-replying');
      this.$emit('start-editing');
    },
    startEditing() {
      this.$emit('start-editing');
      this.isEditing = true;
      const currentDraft = getDraft(this.autosaveKey);
      // Prevent accidental overwriting of
      // draft in case it is already present.
      if (!currentDraft) {
        updateDraft(this.autosaveKey, this.note.body);
      }
    },
    handleEditNote({ note }) {
      if (this.hasAdminPermission && note.id === this.note.id) {
        this.startEditing();
      }
    },
    async updateNote({ commentText, executeOptimisticResponse = true }) {
      try {
        this.isEditing = false;
        this.isUpdating = true;

        await this.$apollo.mutate({
          mutation: updateWorkItemNoteMutation,
          variables: {
            input: {
              id: this.note.id,
              body: commentText,
            },
          },
          // Ignore this when toggling checkbox https://gitlab.com/gitlab-org/gitlab/-/issues/521723
          optimisticResponse: executeOptimisticResponse
            ? {
                updateNote: {
                  errors: [],
                  note: {
                    ...this.note,
                    bodyHtml: renderMarkdown(commentText),
                  },
                },
              }
            : undefined,
        });
        clearDraft(this.autosaveKey);
        clearDraft(this.autosaveKeyInternalNote);
      } catch (error) {
        updateDraft(this.autosaveKey, commentText);
        this.isEditing = true;
        this.$emit('error', __('Something went wrong when updating a comment. Please try again'));
        Sentry.captureException(error);
      } finally {
        this.isUpdating = false;
      }
    },
    getNewAssigneesAndWidget() {
      let newAssignees = [];
      if (this.isAuthorAnAssignee) {
        newAssignees = this.assignees.filter(({ id }) => id !== this.author.id);
      } else {
        newAssignees = [...this.assignees, this.author];
      }

      const assigneesWidgetIndex = this.workItem.widgets.findIndex(isAssigneesWidget);

      const editedWorkItemWidgets = [...this.workItem.widgets];

      editedWorkItemWidgets[assigneesWidgetIndex] = {
        ...editedWorkItemWidgets[assigneesWidgetIndex],
        assignees: {
          nodes: newAssignees,
        },
      };

      const editedFeatures = this.workItem?.features
        ? {
            features: {
              ...this.workItem.features,
              assignees: {
                ...(this.workItem.features.assignees || {}),
                assignees: {
                  nodes: newAssignees,
                },
              },
            },
          }
        : {};

      return {
        newAssignees,
        editedWorkItemWidgets,
        editedFeatures,
      };
    },
    notifyCopyDone() {
      toast(__('Link copied to clipboard.'));
    },
    async assignUserAction() {
      const { newAssignees, editedWorkItemWidgets, editedFeatures } =
        this.getNewAssigneesAndWidget();
      const useWorkItemFeatures = Boolean(this.glFeatures?.workItemFeaturesField);

      try {
        await this.$apollo.mutate({
          mutation: updateWorkItemMutation,
          variables: {
            input: {
              id: this.workItemId,
              assigneesWidget: {
                assigneeIds: newAssignees.map(({ id }) => id),
              },
            },
            useWorkItemFeatures,
          },
          optimisticResponse: {
            workItemUpdate: {
              errors: [],
              workItem: {
                ...this.workItem,
                widgets: editedWorkItemWidgets,
                ...editedFeatures,
              },
            },
          },
        });
        this.track(`${this.isAuthorAnAssignee ? 'unassigned_user' : 'assigned_user'}`);
      } catch (error) {
        this.$emit('error', i18n.updateError);
        Sentry.captureException(error);
      }
    },
    cancelEditing() {
      this.isEditing = false;
      this.$emit('cancel-editing');
    },
  },
};
</script>

<template>
  <!-- `discussion-id` has to reach the `.js-timeline-entry` root, where the quote-reply
  shortcut reads it (getDiscussionIdFromSelection in work_item_notes.vue). It used to
  arrive by attribute fallthrough, which stopped once the prop was declared below. -->
  <timeline-entry-item :id="noteAnchorId" :class="entryClass" :discussion-id="discussionId">
    <div :key="note.id" class="timeline-avatar gl-float-left">
      <gl-avatar-link
        :href="author.webUrl"
        :data-user-id="authorId"
        :data-username="author.username"
        class="js-user-link"
      >
        <gl-avatar
          :src="author.avatarUrl"
          :entity-name="author.username"
          :alt="author.name"
          :size="32"
        />
      </gl-avatar-link>
    </div>
    <div class="timeline-content" :class="{ 'gl-rounded-b-lg': isOnlyCommentOfAThread }">
      <div data-testid="note-wrapper">
        <div :class="noteHeaderClass">
          <note-header
            :author="author"
            :created-at="note.createdAt"
            :note-id="note.id"
            :note-url="noteUrl"
            :is-internal-note="note.internal"
            :is-imported="note.imported"
            :email-participant="externalAuthor"
          />
          <div class="gl-inline-flex">
            <note-actions
              v-if="!isEditing"
              :full-path="fullPath"
              :show-award-emoji="hasAwardEmojiPermission"
              :work-item-iid="workItemIid"
              :note="note"
              :note-url="noteUrl"
              :show-reply="showReply"
              :show-edit="hasAdminPermission"
              :is-author-an-assignee="isAuthorAnAssignee"
              :show-assign-unassign="canSetWorkItemMetadata && hasAuthor"
              :can-report-abuse="!isCurrentUserAuthorOfNote"
              :is-work-item-author="isWorkItemAuthor"
              :work-item-type="workItemType"
              :is-author-contributor="note.authorIsContributor"
              :max-access-level-of-author="note.maxAccessLevelOfAuthor"
              :project-name="projectName"
              :can-resolve="canResolve"
              :is-resolved="isDiscussionResolved"
              :is-resolving="isResolving"
              :resolved-by="resolvedBy"
              :duo-session-id="duoCreatedSessionId"
              @start-replying="showReplyForm"
              @start-editing="startEditing"
              @resolve="$emit('resolve')"
              @error="($event) => $emit('error', $event)"
              @notify-copy-done="notifyCopyDone"
              @delete-note="$emit('delete-note')"
              @assign-user="assignUserAction"
              @report-abuse="$emit('report-abuse')"
            />
          </div>
        </div>
        <div class="note-body">
          <work-item-comment-form
            v-if="isEditing"
            :work-item-type="workItemType"
            :aria-label="__('Edit comment')"
            :autosave-key="autosaveKey"
            :autosave-key-internal-note="autosaveKeyInternalNote"
            :initial-value="note.body"
            :comment-button-text="__('Save comment')"
            :autocomplete-data-sources="autocompleteDataSources"
            :markdown-preview-path="markdownPreviewPath"
            :new-comment-template-paths="newCommentTemplatePaths"
            :work-item-id="workItemId"
            :autofocus="isEditing"
            :is-work-item-confidential="isWorkItemConfidential"
            :is-discussion-resolved="isDiscussionResolved"
            :is-discussion-resolvable="isDiscussionResolvable"
            :has-replies="hasReplies"
            :full-path="fullPath"
            :hide-fullscreen-markdown-button="hideFullscreenMarkdownButton"
            :uploads-path="uploadsPath"
            class="gl-mt-3"
            @cancel-editing="cancelEditing"
            @toggle-resolve-discussion="$emit('resolve')"
            @submit-form="updateNote"
          />
          <div v-else class="timeline-discussion-body">
            <note-body
              ref="noteBody"
              :note="note"
              :has-admin-note-permission="hasAdminPermission"
              :is-updating="isUpdating"
              @update-note="updateNote"
            />
            <duo-question-note
              v-if="isFirstNote"
              :note="note"
              :work-item-id="workItemId"
              :discussion-id="discussionId"
              :replies="replies"
              :is-discussion-resolved="isDiscussionResolved"
              @error="$emit('error', $event)"
            />
          </div>
          <edited-at
            v-if="note.lastEditedBy && !isEditing"
            :updated-at="note.lastEditedAt"
            :updated-by-name="lastEditedBy.name"
            :updated-by-path="lastEditedBy.webPath"
            class="gl-mt-5"
            :class="isFirstNote ? '' : 'gl-pl-7'"
          />

          <div class="note-awards" :class="isFirstNote ? '' : 'gl-pl-7'">
            <work-item-note-awards-list
              :full-path="fullPath"
              :note="note"
              :work-item-iid="workItemIid"
            />
          </div>
        </div>
      </div>
    </div>
  </timeline-entry-item>
</template>
